import { FunctionComponent, useContext, useEffect, useId, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { getCountries } from "util/network/Countries";
import { CountryType } from "types/CountryType";
import { COUNTRY_CODES, CountryCode } from "./countryCodes";

type PropsType = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
};

// Module-level cache so the enabled-country list is fetched once and shared
// across every mounted CountrySelect. Falls back to the static ISO list.
let countriesPromise: Promise<CountryCode[]> | null = null;

const loadCountries = (token: string): Promise<CountryCode[]> => {
  if (!countriesPromise) {
    countriesPromise = getCountries(token)
      .then((countries: CountryType[]) =>
        countries
          .filter((c) => c.enabled)
          .map((c) => ({ code: c.code, label: c.name })),
      )
      .catch(() => {
        // Reset so a later mount can retry, and fall back to the static list.
        countriesPromise = null;
        return COUNTRY_CODES;
      });
  }
  return countriesPromise;
};

/**
 * Reusable country dropdown. Populated from GET /countries (enabled only),
 * falling back to the static ISO 3166-1 alpha-2 list if the request fails.
 */
const CountrySelect: FunctionComponent<PropsType> = ({
  value,
  onChange,
  label = "Country",
  required = false,
  disabled = false,
  size = "small",
  fullWidth = true,
}) => {
  const labelId = useId();
  const { token } = useContext(AppStateContext);
  const [countries, setCountries] = useState<CountryCode[]>(COUNTRY_CODES);

  useEffect(() => {
    if (!token) return;
    let active = true;
    loadCountries(token).then((list) => {
      if (active && list.length > 0) setCountries(list);
    });
    return () => {
      active = false;
    };
  }, [token]);

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };

  // Guard against the current value not being present in the loaded list
  // (e.g. a disabled country on an existing address) so MUI still renders it.
  const options =
    value && !countries.some((c) => c.code === value)
      ? [{ code: value, label: value }, ...countries]
      : countries;

  return (
    <FormControl size={size} fullWidth={fullWidth} required={required} disabled={disabled}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select labelId={labelId} value={value} label={label} onChange={handleChange}>
        {options.map((country) => (
          <MenuItem key={country.code} value={country.code}>
            {country.label} ({country.code})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CountrySelect;
